import { type TransactionFailedProperties } from '@/ampli';
import { useSignMessage, useSignTransaction } from '@privy-io/react-auth/solana';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { type PublicClient, type WalletClient, parseUnits } from 'viem';
import { useConfig, useSwitchChain, useWalletClient } from 'wagmi';
import { getPublicClient } from 'wagmi/actions';

import { ClientWalletError } from '@/lib/errors/client';

import { ADVANCED_ORDER_TYPE_LABELS, type AdvancedOrderType } from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { limitOrderBuildResponseSchema } from '@/utils/0x.schema';
import type { LimitOrderBuildResponse, LimitOrderSubmitResponse } from '@/utils/0x.types';
import { buildLimitOrder } from '@/utils/0x/orderbook';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import type { ChainAddress, EthereumAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { getSubmissionFailure, isNetworkError } from '@/utils/submissionReason';
import {
  getConnectedChain,
  getWalletClientAfterChainSwitch,
  isUserRejection,
  trySwitchChain,
} from '@/utils/wallet';

import {
  prepareLimitOrderSubmission,
  type EvmLimitOrderExecutionContext,
  type LimitOrderExecutionContext,
  type SvmLimitOrderExecutionContext,
  type SubmitLimitOrderPhase,
} from './useSubmitLimitOrder.utils';

export type { SubmitLimitOrderPhase } from './useSubmitLimitOrder.utils';

function orderNoun(orderType: AdvancedOrderType = 'limit') {
  return `${ADVANCED_ORDER_TYPE_LABELS[orderType]} order`;
}

export type SubmitLimitOrderFailureStage = 'pre_submission' | 'submission';

export type SubmitLimitOrderVariables = {
  chainId: number;
  maker: ChainAddress;
  buyToken: TokenResult;
  sellToken: TokenResult;
  buyAmount: string;
  sellAmount: string;
  expiry: Date;
  orderType?: AdvancedOrderType;
  triggerCrossPrice?: string;
};

export type SubmitLimitOrderResult = {
  provider: LimitOrderBuildResponse['provider'];
  orderIdentifier: string;
};

export type SubmitLimitOrderCoreExecutionContext =
  | Omit<EvmLimitOrderExecutionContext, 'takerAmount'>
  | SvmLimitOrderExecutionContext;

type SubmitLimitOrderErrorOptions = {
  failureStage: SubmitLimitOrderFailureStage;
  failureReason: TransactionFailedProperties['failure_reason'];
  submissionReason?: string;
  revertReason?: string;
  orderIdentifier?: string;
  cause: unknown;
};

export class SubmitLimitOrderMutationError extends Error {
  readonly failureStage: SubmitLimitOrderFailureStage;
  readonly failureReason: TransactionFailedProperties['failure_reason'];
  readonly submissionReason: string | undefined;
  readonly revertReason: string | undefined;
  readonly orderIdentifier: string | undefined;
  readonly originalCause: unknown;

  constructor(message: string, options: SubmitLimitOrderErrorOptions) {
    super(message, options.cause instanceof Error ? { cause: options.cause } : undefined);
    this.name = 'SubmitLimitOrderMutationError';
    this.failureStage = options.failureStage;
    this.failureReason = options.failureReason;
    this.submissionReason = options.submissionReason;
    this.revertReason = options.revertReason;
    this.orderIdentifier = options.orderIdentifier;
    this.originalCause = options.cause;
  }
}

export class LimitOrderSubmissionHttpError extends Error {
  constructor(
    readonly status: number,
    readonly failureReason: TransactionFailedProperties['failure_reason'],
    orderType: AdvancedOrderType = 'limit',
  ) {
    super(`${orderNoun(orderType)} submission failed (${status})`);
    this.name = 'LimitOrderSubmissionHttpError';
  }
}

export const getOrderbookSubmissionFailureReason = async (
  response: Response,
): Promise<TransactionFailedProperties['failure_reason']> => {
  let reason: string | undefined;
  try {
    reason = ((await response.clone().json()) as { reason?: string })?.reason;
  } catch {
    // A non-JSON response has no stable upstream marker.
  }
  if (reason === 'ORDERBOOK_SUBMISSION_FAILED') {
    return response.status >= 500 ? 'orderbook_unavailable' : 'orderbook_rejected';
  }
  return 'submission_error';
};

export const toPreSubmissionError = (
  cause: unknown,
  orderIdentifier?: string,
  orderType: AdvancedOrderType = 'limit',
): SubmitLimitOrderMutationError => {
  const rejected = isUserRejection(cause);
  const failure = rejected ? undefined : getSubmissionFailure(cause);
  return new SubmitLimitOrderMutationError(`${orderNoun(orderType)} preparation failed`, {
    failureStage: 'pre_submission',
    failureReason: rejected ? 'user_rejected' : 'submission_error',
    submissionReason: failure?.submissionReason,
    revertReason: failure?.revertReason,
    orderIdentifier,
    cause,
  });
};

export const toSubmissionError = (
  cause: unknown,
  orderIdentifier?: string,
  orderType: AdvancedOrderType = 'limit',
): SubmitLimitOrderMutationError => {
  const failureReason: TransactionFailedProperties['failure_reason'] =
    cause instanceof LimitOrderSubmissionHttpError
      ? cause.failureReason
      : cause instanceof Error && isNetworkError(cause)
        ? 'network_error'
        : 'submission_error';

  const failure =
    cause instanceof LimitOrderSubmissionHttpError
      ? { submissionReason: `http_${cause.status}` }
      : getSubmissionFailure(cause);

  return new SubmitLimitOrderMutationError(`${orderNoun(orderType)} submission failed`, {
    failureStage: 'submission',
    failureReason,
    submissionReason: failure.submissionReason,
    revertReason: failure.revertReason,
    orderIdentifier,
    cause,
  });
};

export async function submitLimitOrderCore(
  variables: SubmitLimitOrderVariables,
  executionContext: SubmitLimitOrderCoreExecutionContext,
): Promise<SubmitLimitOrderResult> {
  let orderIdentifier: string | undefined;
  const orderType = variables.orderType ?? 'limit';

  try {
    if (executionContext.chainId !== variables.chainId) {
      throw new ClientWalletError(
        `Submission context chain does not match the requested ${orderNoun(orderType)}`,
      );
    }

    const makerAmount = parseUnits(variables.sellAmount, variables.sellToken.decimals);
    const takerAmount = parseUnits(variables.buyAmount, variables.buyToken.decimals);

    executionContext.setPhase('building');
    let currentOrder: {
      expiry: bigint;
      maker: ChainAddress;
      makerAmount: bigint;
      makerToken: ChainAddress;
      takerAmount: bigint;
      takerToken: ChainAddress;
    };
    switch (executionContext.chainFamily) {
      case 'evm':
        if (variables.chainId === CHAIN_IDS.SOLANA) {
          throw new ClientWalletError(
            `EVM submission context cannot build a Solana ${orderNoun(orderType)}`,
          );
        }
        currentOrder = await buildLimitOrder(
          {
            expiry: variables.expiry,
            maker: variables.maker as EthereumAddress,
            makerAmount,
            makerToken: variables.sellToken.address as EthereumAddress,
            takerAmount,
            takerToken: variables.buyToken.address as EthereumAddress,
          },
          variables.chainId,
        );
        break;
      case 'svm':
        if (variables.chainId !== CHAIN_IDS.SOLANA) {
          throw new ClientWalletError('Solana submission context requires the Solana chain');
        }
        currentOrder = {
          expiry: BigInt(Math.floor(variables.expiry.getTime() / 1000)),
          maker: variables.maker,
          makerAmount,
          makerToken: variables.sellToken.address,
          takerAmount,
          takerToken: variables.buyToken.address,
        };
        break;
      default: {
        const exhaustiveContext: never = executionContext;
        return exhaustiveContext;
      }
    }

    const buildResponseRaw = await fetch(routes.api.LIMIT_ORDER_BUILD, {
      method: 'POST',
      body: JSON.stringify({
        makerAsset: currentOrder.makerToken,
        takerAsset: currentOrder.takerToken,
        makingAmount: currentOrder.makerAmount.toString(),
        takingAmount: currentOrder.takerAmount.toString(),
        maker: currentOrder.maker,
        chainId: variables.chainId,
        expiration: currentOrder.expiry.toString(),
        makerDecimals: variables.sellToken.decimals,
        takerDecimals: variables.buyToken.decimals,
        orderType,
        ...(variables.triggerCrossPrice ? { triggerCrossPrice: variables.triggerCrossPrice } : {}),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!buildResponseRaw.ok) {
      throw new Error(`Failed to build ${orderNoun(orderType)} (${buildResponseRaw.status})`);
    }
    const buildResponse = limitOrderBuildResponseSchema.parse(await buildResponseRaw.json());
    const prepareContext: LimitOrderExecutionContext =
      executionContext.chainFamily === 'evm'
        ? { ...executionContext, takerAmount }
        : executionContext;
    const prepared = await prepareLimitOrderSubmission(buildResponse, prepareContext);
    orderIdentifier = prepared.orderIdentifier;

    executionContext.setPhase('submitting');
    let submitResponse: LimitOrderSubmitResponse;
    try {
      const submitResponseRaw = await fetch(routes.api.LIMIT_ORDER, {
        method: 'POST',
        body: JSON.stringify(prepared.request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!submitResponseRaw.ok) {
        throw new LimitOrderSubmissionHttpError(
          submitResponseRaw.status,
          await getOrderbookSubmissionFailureReason(submitResponseRaw),
          orderType,
        );
      }
      submitResponse = (await submitResponseRaw.json()) as LimitOrderSubmitResponse;
      if (submitResponse.provider === 'definitive') {
        orderIdentifier = submitResponse.orderId;
      }
      if (!orderIdentifier) {
        throw new Error(`${orderNoun(orderType)} submission returned no order identifier`);
      }
    } catch (cause) {
      throw toSubmissionError(cause, orderIdentifier, orderType);
    }

    return {
      provider: submitResponse.provider,
      orderIdentifier,
    };
  } catch (cause) {
    if (cause instanceof SubmitLimitOrderMutationError) throw cause;
    throw toPreSubmissionError(cause, orderIdentifier, orderType);
  }
}

export function useSubmitLimitOrder(): {
  submitLimitOrder: (variables: SubmitLimitOrderVariables) => Promise<SubmitLimitOrderResult>;
  phase: SubmitLimitOrderPhase;
  isPending: boolean;
  error: SubmitLimitOrderMutationError | null;
} {
  const [phase, setPhase] = useState<SubmitLimitOrderPhase>('idle');
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const { signMessage: signSolanaMessage } = useSignMessage();
  const { signTransaction: signSolanaTransaction } = useSignTransaction();
  const walletClientQuery = useWalletClient();
  const { switchChainAsync } = useSwitchChain();
  const wagmiConfig = useConfig();

  const mutation = useMutation<
    SubmitLimitOrderResult,
    SubmitLimitOrderMutationError,
    SubmitLimitOrderVariables
  >({
    retry: false,
    mutationFn: async (variables) => {
      try {
        const isSolana = variables.chainId === CHAIN_IDS.SOLANA;
        let executionContext: SubmitLimitOrderCoreExecutionContext;
        if (isSolana) {
          if (!solanaWallet) {
            throw new ClientWalletError('Solana wallet connection is unavailable');
          }
          executionContext = {
            chainFamily: 'svm',
            chainId: CHAIN_IDS.SOLANA,
            solanaWallet,
            signMessage: signSolanaMessage,
            signTransaction: signSolanaTransaction,
            setPhase,
          };
        } else {
          if (!ethereumWallet) {
            throw new ClientWalletError('Wallet connection is unavailable');
          }
          const connectedChain = getConnectedChain(ethereumWallet);
          let walletClient = walletClientQuery.data as WalletClient | undefined;
          if (connectedChain?.id !== variables.chainId) {
            setPhase('switching-chain');
            const switchResult = await trySwitchChain({
              chainId: variables.chainId,
              switchChainAsync,
            });
            if (!switchResult.success) {
              const switchError = new ClientWalletError(
                `Failed to switch wallet chain: ${switchResult.reason}`,
              );
              if (switchResult.reason === 'user_rejected') {
                Object.assign(switchError, { code: 4001 });
              }
              throw switchError;
            }
            walletClient = (await getWalletClientAfterChainSwitch(variables.chainId)) as
              | WalletClient
              | undefined;
          }
          if (!walletClient) {
            throw new ClientWalletError('Wallet client unavailable');
          }
          if (walletClient.chain?.id !== variables.chainId) {
            throw new ClientWalletError(
              `Wallet client chain does not match the ${orderNoun(variables.orderType)}`,
            );
          }
          executionContext = {
            chainFamily: 'evm',
            chainId: variables.chainId,
            walletClient,
            publicClient: getPublicClient(wagmiConfig, {
              chainId: variables.chainId,
            }) as Pick<PublicClient, 'waitForTransactionReceipt'> | undefined,
            setPhase,
          };
        }

        return await submitLimitOrderCore(variables, executionContext);
      } catch (cause) {
        if (cause instanceof SubmitLimitOrderMutationError) throw cause;
        throw toPreSubmissionError(cause, undefined, variables.orderType);
      }
    },
    onSettled: () => {
      setPhase('idle');
    },
  });

  return {
    submitLimitOrder: mutation.mutateAsync,
    phase,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
